import React, { useMemo, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getStatLabel } from "../../utils/queryBuilder";

const QueryStatFilterSection = ({
  availableStats,
  selectedStats,
  statisticalFilters,
  catalog,
  onToggleStat,
  onFilterChange,
}) => {
  const [search, setSearch] = useState("");

  const filteredStats = useMemo(() => {
    const stats = availableStats || [];
    const term = search.trim().toLowerCase();
    if (!term) return stats;
    return stats.filter((stat) =>
      getStatLabel(stat, catalog).toLowerCase().includes(term)
    );
  }, [availableStats, catalog, search]);

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Search stats..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Stack spacing={1} sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5 }}>
        {filteredStats.map((stat) => {
          const selected = selectedStats.includes(stat);
          const filter = statisticalFilters[stat] || { operator: ">=", value: "" };

          return (
            <Box
              key={stat}
              sx={{
                border: "1px solid",
                borderColor: selected ? "primary.main" : "divider",
                borderRadius: 1.5,
                p: 1.25,
                bgcolor: selected ? "action.selected" : "transparent",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  size="small"
                  checked={selected}
                  onChange={() => onToggleStat(stat)}
                />
                <Typography variant="body2" fontWeight={selected ? 600 : 400}>
                  {getStatLabel(stat, catalog)}
                </Typography>
              </Stack>

              {selected && (
                <Stack direction="row" spacing={1} sx={{ mt: 1, pl: 4.5 }}>
                  <FormControl size="small" sx={{ minWidth: 72 }}>
                    <InputLabel>Op</InputLabel>
                    <Select
                      value={filter.operator || ">="}
                      label="Op"
                      onChange={(e) => onFilterChange(stat, "operator", e.target.value)}
                    >
                      <MenuItem value=">=">≥</MenuItem>
                      <MenuItem value="<=">≤</MenuItem>
                      <MenuItem value="=">=</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="number"
                    label="Value"
                    value={filter.value ?? ""}
                    onChange={(e) => onFilterChange(stat, "value", e.target.value)}
                    sx={{ flex: 1 }}
                    inputProps={{ step: stat.includes("Average") || stat.includes("Percentage") ? 0.001 : 1 }}
                  />
                </Stack>
              )}
            </Box>
          );
        })}

        {filteredStats.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No stats match your search.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default QueryStatFilterSection;
